import { ApiResponseError, apiFetch, readApiJson } from '@/lib/api-fetch'
import {
  GoldExhibitionDetailResponseSchema,
  GoldExhibitionArchiveResponseSchema,
  GoldExhibitionArchivedRecordSchema,
  GoldExhibitionListQuerySchema,
  GoldExhibitionListResponseSchema,
  GoldExhibitionNameRequestSchema,
  GoldExhibitionPurchaseBatchRequestSchema,
  GoldExhibitionPurchaseBatchResponseSchema,
  GoldExhibitionPurchaseRowRequestSchema,
  GoldExhibitionPurchaseRowResponseSchema,
  GoldExhibitionSupplierRequestSchema,
  GoldExhibitionSupplierResponseSchema,
  GoldExhibitionSupplierUpdateRequestSchema,
  GoldExhibitionUpdateRequestSchema,
  type GoldExhibitionDetail,
  type GoldExhibitionArchiveResponse,
  type GoldExhibitionArchivedRecord,
  type GoldExhibitionListQuery,
  type GoldExhibitionListResponse,
  type GoldExhibitionPurchaseRow,
  type GoldExhibitionPurchaseRowRequest,
  type GoldExhibitionPurchaseBatch,
  type GoldExhibitionSupplier,
} from '@/lib/api/schemas/gold-exhibitions.schema'

const GOLD_EXHIBITIONS_PATH = '/api/inventory/gold-exhibitions'

function exhibitionPath(exhibitionId: string): string {
  return `${GOLD_EXHIBITIONS_PATH}/${encodeURIComponent(exhibitionId)}`
}

function purchaseRowPath(exhibitionId: string, rowId: string): string {
  return `${exhibitionPath(exhibitionId)}/rows/${encodeURIComponent(rowId)}`
}

function jsonRequest(method: 'POST' | 'PATCH' | 'PUT', body: unknown, signal?: AbortSignal): RequestInit {
  return {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  }
}

async function readDetail(response: Response): Promise<GoldExhibitionDetail> {
  return GoldExhibitionDetailResponseSchema.parse(await readApiJson(response)).item
}

export async function listGoldExhibitions(
  query: GoldExhibitionListQuery = {},
  signal?: AbortSignal,
): Promise<GoldExhibitionListResponse> {
  const parsedQuery = GoldExhibitionListQuerySchema.parse(query)
  const parameters = new URLSearchParams()
  parameters.set('limit', String(parsedQuery.limit ?? 50))
  if (parsedQuery.cursor !== undefined) parameters.set('cursor', parsedQuery.cursor)
  const response = await apiFetch(`${GOLD_EXHIBITIONS_PATH}?${parameters.toString()}`, { signal })
  return GoldExhibitionListResponseSchema.parse(await readApiJson(response))
}

export async function createGoldExhibition(
  input: { name: string; budgetKopecks?: number | null; startsOn?: string | null; endsOn?: string | null; imageUrl?: string | null },
  signal?: AbortSignal,
): Promise<GoldExhibitionDetail> {
  const body = GoldExhibitionNameRequestSchema.parse(input)
  return readDetail(await apiFetch(GOLD_EXHIBITIONS_PATH, jsonRequest('POST', body, signal)))
}

export async function getGoldExhibition(exhibitionId: string, signal?: AbortSignal): Promise<GoldExhibitionDetail> {
  return readDetail(await apiFetch(exhibitionPath(exhibitionId), { signal }))
}

export async function listGoldExhibitionArchive(
  query: GoldExhibitionListQuery = {},
  signal?: AbortSignal,
): Promise<GoldExhibitionArchiveResponse> {
  const parsedQuery = GoldExhibitionListQuerySchema.parse(query)
  const parameters = new URLSearchParams()
  parameters.set('limit', String(parsedQuery.limit ?? 50))
  if (parsedQuery.cursor !== undefined) parameters.set('cursor', parsedQuery.cursor)
  const response = await apiFetch(`${GOLD_EXHIBITIONS_PATH}/archive?${parameters.toString()}`, { signal })
  return GoldExhibitionArchiveResponseSchema.parse(await readApiJson(response))
}

export async function archiveGoldExhibition(exhibitionId: string, signal?: AbortSignal): Promise<GoldExhibitionArchivedRecord> {
  const response = await apiFetch(`${exhibitionPath(exhibitionId)}/archive`, { method: 'POST', signal })
  return GoldExhibitionArchivedRecordSchema.parse(await readApiJson(response))
}

export async function restoreGoldExhibition(exhibitionId: string, signal?: AbortSignal): Promise<GoldExhibitionDetail> {
  return readDetail(await apiFetch(`${exhibitionPath(exhibitionId)}/restore`, { method: 'POST', signal }))
}

export async function archiveGoldExhibitionSupplier(exhibitionId: string, supplierId: string, signal?: AbortSignal): Promise<GoldExhibitionArchivedRecord> {
  const response = await apiFetch(`${exhibitionPath(exhibitionId)}/suppliers/${encodeURIComponent(supplierId)}/archive`, { method: 'POST', signal })
  return GoldExhibitionArchivedRecordSchema.parse(await readApiJson(response))
}

export async function restoreGoldExhibitionSupplier(exhibitionId: string, supplierId: string, signal?: AbortSignal): Promise<GoldExhibitionSupplier> {
  const response = await apiFetch(`${exhibitionPath(exhibitionId)}/suppliers/${encodeURIComponent(supplierId)}/restore`, { method: 'POST', signal })
  return GoldExhibitionSupplierResponseSchema.parse(await readApiJson(response)).item
}

export async function renameGoldExhibition(
  exhibitionId: string,
  input: { name: string },
  signal?: AbortSignal,
): Promise<GoldExhibitionDetail> {
  const body = GoldExhibitionNameRequestSchema.parse(input)
  return readDetail(await apiFetch(exhibitionPath(exhibitionId), jsonRequest('PATCH', body, signal)))
}

export async function updateGoldExhibition(
  exhibitionId: string,
  input: { name?: string; budgetKopecks?: number | null; startsOn?: string | null; endsOn?: string | null; imageUrl?: string | null },
  signal?: AbortSignal,
): Promise<GoldExhibitionDetail> {
  const body = GoldExhibitionUpdateRequestSchema.parse(input)
  return readDetail(await apiFetch(exhibitionPath(exhibitionId), jsonRequest('PATCH', body, signal)))
}

export async function createGoldExhibitionSupplier(
  exhibitionId: string,
  input: { name: string; manufacturerId: string | null; priceKopecksPerGram: number | null },
  signal?: AbortSignal,
): Promise<GoldExhibitionSupplier> {
  const body = GoldExhibitionSupplierRequestSchema.parse(input)
  const response = await apiFetch(`${exhibitionPath(exhibitionId)}/suppliers`, jsonRequest('POST', body, signal))
  return GoldExhibitionSupplierResponseSchema.parse(await readApiJson(response)).item
}

export async function updateGoldExhibitionSupplier(
  exhibitionId: string,
  supplierId: string,
  input: { priceKopecksPerGram: number },
  signal?: AbortSignal,
): Promise<GoldExhibitionSupplier> {
  const body = GoldExhibitionSupplierUpdateRequestSchema.parse(input)
  const response = await apiFetch(`${exhibitionPath(exhibitionId)}/suppliers/${encodeURIComponent(supplierId)}`, jsonRequest('PATCH', body, signal))
  return GoldExhibitionSupplierResponseSchema.parse(await readApiJson(response)).item
}

export async function createGoldExhibitionPurchaseBatch(
  exhibitionId: string,
  supplierId: string,
  input: { weightMilliGrams: number; priceKopecksPerGram?: number },
  signal?: AbortSignal,
): Promise<GoldExhibitionPurchaseBatch> {
  const body = GoldExhibitionPurchaseBatchRequestSchema.parse(input)
  const response = await apiFetch(`${exhibitionPath(exhibitionId)}/suppliers/${encodeURIComponent(supplierId)}/batches`, jsonRequest('POST', body, signal))
  return GoldExhibitionPurchaseBatchResponseSchema.parse(await readApiJson(response)).item
}

function purchaseBatchPath(exhibitionId: string, supplierId: string, batchId: string): string {
  return `${exhibitionPath(exhibitionId)}/suppliers/${encodeURIComponent(supplierId)}/batches/${encodeURIComponent(batchId)}`
}

export async function updateGoldExhibitionPurchaseBatch(
  exhibitionId: string,
  supplierId: string,
  batchId: string,
  input: { weightMilliGrams: number; priceKopecksPerGram?: number },
  signal?: AbortSignal,
): Promise<GoldExhibitionPurchaseBatch> {
  const body = GoldExhibitionPurchaseBatchRequestSchema.parse(input)
  const response = await apiFetch(purchaseBatchPath(exhibitionId, supplierId, batchId), jsonRequest('PUT', body, signal))
  return GoldExhibitionPurchaseBatchResponseSchema.parse(await readApiJson(response)).item
}

export async function deleteGoldExhibitionPurchaseBatch(
  exhibitionId: string,
  supplierId: string,
  batchId: string,
  signal?: AbortSignal,
): Promise<void> {
  await expectNoContent(await apiFetch(purchaseBatchPath(exhibitionId, supplierId, batchId), { method: 'DELETE', signal }))
}

export async function createGoldExhibitionPurchaseRow(
  exhibitionId: string,
  input: GoldExhibitionPurchaseRowRequest,
  signal?: AbortSignal,
): Promise<GoldExhibitionPurchaseRow> {
  const body = GoldExhibitionPurchaseRowRequestSchema.parse(input)
  const response = await apiFetch(`${exhibitionPath(exhibitionId)}/rows`, jsonRequest('POST', body, signal))
  return GoldExhibitionPurchaseRowResponseSchema.parse(await readApiJson(response)).item
}

export async function updateGoldExhibitionPurchaseRow(
  exhibitionId: string,
  rowId: string,
  input: GoldExhibitionPurchaseRowRequest,
  signal?: AbortSignal,
): Promise<GoldExhibitionPurchaseRow> {
  const body = GoldExhibitionPurchaseRowRequestSchema.parse(input)
  const response = await apiFetch(purchaseRowPath(exhibitionId, rowId), jsonRequest('PUT', body, signal))
  return GoldExhibitionPurchaseRowResponseSchema.parse(await readApiJson(response)).item
}

async function expectNoContent(response: Response): Promise<void> {
  if (!response.ok) {
    await readApiJson(response)
    throw new ApiResponseError(response.status)
  }
  if (response.status !== 204) throw new ApiResponseError(response.status)
}

export async function deleteGoldExhibitionPurchaseRow(
  exhibitionId: string,
  rowId: string,
  signal?: AbortSignal,
): Promise<void> {
  await expectNoContent(await apiFetch(purchaseRowPath(exhibitionId, rowId), { method: 'DELETE', signal }))
}

export async function clearGoldExhibitionPurchaseRows(
  exhibitionId: string,
  signal?: AbortSignal,
): Promise<GoldExhibitionDetail> {
  return readDetail(await apiFetch(`${exhibitionPath(exhibitionId)}/rows`, { method: 'DELETE', signal }))
}
